import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { cvManagementApi } from '../../../services/api';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, LevelsGrid, LevelCard, LevelCardContent, LevelTitle, LevelDetailsGrid, OrderBadge, ActionButtons, ItalicText } from './styles';
import { COLORS } from '../../../constants/radar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const LanguageLevel = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentLevel, setCurrentLevel] = useState({ level: '', description: '', order: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    fetchLanguageLevels();
  }, []);

  const fetchLanguageLevels = async () => {
    try {
      setLoading(true);
      const data = await cvManagementApi.getAllLanguageLevels();
      setLevels(data);
    } catch (err) {
      showAlert('Failed to load language levels.', 'error', err);
      console.error('Error fetching language levels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (level = null) => {
    if (level) {
      setCurrentLevel(level);
      setIsEditing(true);
    } else {
      setCurrentLevel({ level: '', description: '', order: levels.length + 1 });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (level) => {
    setCurrentLevel(level);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentLevel({
      ...currentLevel,
      [name]: name === 'order' ? parseInt(value, 10) || 0 : value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await cvManagementApi.updateLanguageLevel(currentLevel.id, currentLevel);
        showAlert('Language level updated successfully', 'success');
      } else {
        await cvManagementApi.createLanguageLevel(currentLevel);
        showAlert('Language level created successfully', 'success');
      }
      handleCloseDialog();
      await fetchLanguageLevels();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} language level. Please try again.`, 'error', err);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} language level:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await cvManagementApi.deleteLanguageLevel(currentLevel.id);
      showAlert('Language level deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchLanguageLevels();
    } catch (err) {
      showAlert(err.message || 'Failed to delete language level. Please try again.', 'error', err);
      console.error('Error deleting language level:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLevels = levels.filter(level => 
    level.level.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    level.description?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && levels.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Language Level Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Language Levels"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>

            <HeaderButton 
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Level
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{ 
            color: '#64748B', 
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredLevels.length}
            </span>
            {filteredLevels.length === 1 ? 'level' : 'levels'} found
          </div>

          {filteredLevels.length === 0 ? (
            <EmptyState
              title="No language levels found"
              description={searchTerm ? 
                "No language levels match your search criteria. Try adjusting your search term." : 
                "No language levels have been added yet. Click the '+ Add Level' button to add your first language level."}
            />
          ) : (
            <LevelsGrid>
              {filteredLevels.map((level) => (
                <LevelCard key={level.id}>
                  <LevelCardContent>
                    <LevelTitle>
                      {level.level}
                      <OrderBadge>
                        Order: {level.order}
                      </OrderBadge>
                    </LevelTitle>
                    <LevelDetailsGrid>
                      {level.description ? level.description : <ItalicText>No description provided</ItalicText>}
                    </LevelDetailsGrid>
                    <ActionButtons>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(level)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDeleteDialog(level)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </LevelCardContent>
                </LevelCard>
              ))}
            </LevelsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Language Level' : 'Add Language Level'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="level"
            label="Level Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentLevel.level}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={currentLevel.description}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentLevel.order}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!currentLevel.level.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the language level "{currentLevel.level}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LanguageLevel; 